import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteShell from "./SiteShell";
import Seo, { createBreadcrumbList, createImagePageSchema } from "./Seo";
import { getImagePagePath } from "./imageRoutes";
import { newestFirst } from "./imageOrdering";
import seoHubs from "./seoHubs.json";
import {
  createAlternateLinks,
  getBrowserLocale,
  getCategoryPath,
  getLocalizedPath,
  localizeCategory,
  localizeImage,
  translate,
} from "./i18n";
import styles from "./SiteShell.module.css";

function getHubTranslation(hub, locale) {
  return hub.translations?.[locale] || hub.translations?.de;
}

function localizeHub(hub, locale) {
  const translation = getHubTranslation(hub, locale);

  return {
    ...hub,
    ...translation,
    path: getLocalizedPath(`/themen/${translation.slug}`, locale),
  };
}

function ImageDetail({ forcedLocale }) {
  const params = useParams();
  const downloadPath = params["*"];
  const seoSlug = params.seoSlug;
  const downloadUrl = downloadPath ? `${downloadPath}.png` : null;
  const locale = forcedLocale || getBrowserLocale();
  const t = (key, values) => translate(locale, key, values);
  const localPath = (path) => getLocalizedPath(path, locale);
  const [viewModel, setViewModel] = useState({
    category: null,
    image: null,
    relatedImages: [],
    relatedHubs: [],
  });
  const [status, setStatus] = useState("loading");
  const imageCanvasRef = useRef(null);
  const { category, image, relatedImages, relatedHubs } = viewModel;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch("/db.json", { cache: "no-store" });
        const json = await response.json();
        const rawImage = json.images.find((item) =>
          seoSlug
            ? item.seoSlug === seoSlug ||
              item.seoSlugs?.[locale] === seoSlug ||
              Object.values(item.seoSlugs || {}).includes(seoSlug)
            : item.downloadUrl === downloadUrl
        );

        if (!rawImage) {
          setStatus("missing");
          return;
        }

        const rawCategory =
          json.categories.find((item) => item.tag === rawImage.tag) || null;
        const image = localizeImage(rawImage, locale);
        const category = rawCategory ? localizeCategory(rawCategory, locale) : null;
        const relatedImages = newestFirst(
          json.images.filter(
            (item) =>
              item.tag === rawImage.tag && item.downloadUrl !== rawImage.downloadUrl
          )
        )
          .slice(0, 8)
          .map((item) => localizeImage(item, locale));
        const relatedHubs = seoHubs
          .filter((hub) => hub.categoryTags.includes(rawImage.tag))
          .slice(0, 4)
          .map((hub) => localizeHub(hub, locale));

        setViewModel({ category, image, relatedImages, relatedHubs });
        setStatus("ready");
      } catch {
        setStatus("error");
      }
    };

    fetchData();
  }, [downloadUrl, locale, seoSlug]);

  useEffect(() => {
    if (status !== "ready" || !imageCanvasRef.current) {
      return;
    }

    imageCanvasRef.current.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, [status, image?.downloadUrl]);

  const pagePath = image
    ? getImagePagePath(image, locale)
    : seoSlug
    ? localPath(`/ausmalbild/${seoSlug}`)
    : localPath(`/view/${downloadPath}`);
  const pageTitle = image
    ? t("imagePageTitle", { title: image.title })
    : status === "missing"
    ? t("imageMissingPageTitle")
    : t("imageLoadingPageTitle");
  const pageDescription = image
    ? t("imagePageDescription", { description: image.description })
    : t("freeColoringPages");
  const categoryName = category ? category.name : image?.tag;
  const alternateLinks = image
    ? createAlternateLinks((targetLocale) => getImagePagePath(image, targetLocale))
    : [];

  return (
    <SiteShell
      locale={locale}
      localePathResolver={
        image ? (targetLocale) => getImagePagePath(image, targetLocale) : undefined
      }
    >
      <Seo
        title={pageTitle}
        description={pageDescription}
        path={pagePath}
        image={image ? `/${image.downloadUrl}` : undefined}
        noindex={status === "missing" || status === "error"}
        alternates={alternateLinks}
        preserveExistingHead={status === "loading"}
        structuredData={
          image
            ? [
                ...createImagePageSchema({
                  image,
                  categoryName,
                  path: pagePath,
                  keywordLabels: [
                    t("imageKeywordColoringPage"),
                    t("imageKeywordPrintable"),
                  ],
                  licensePath: localPath("/nutzung-und-lizenz"),
                }),
                createBreadcrumbList([
                  { name: t("homeTitle"), path: localPath("/") },
                  ...(category
                    ? [
                        {
                          name: category.name,
                          path: getCategoryPath(category, locale),
                        },
                      ]
                    : []),
                  { name: image.title, path: pagePath },
                ]),
              ]
            : []
        }
      />
      {status === "loading" && (
        <section className={styles.statusPanel}>
          <h1 className={styles.statusTitle}>{t("detailLoadingTitle")}</h1>
          <p className={styles.statusText}>{t("detailLoadingText")}</p>
        </section>
      )}

      {status === "error" && (
        <section className={styles.statusPanel}>
          <h1 className={styles.statusTitle}>{t("detailErrorTitle")}</h1>
          <p className={styles.statusText}>{t("detailErrorText")}</p>
          <div className={styles.buttonRow}>
            <Link className={styles.buttonPrimary} to={localPath("/")}>
              {t("backToGallery")}
            </Link>
          </div>
        </section>
      )}

      {status === "missing" && (
        <section className={styles.statusPanel}>
          <h1 className={styles.statusTitle}>{t("detailMissingTitle")}</h1>
          <p className={styles.statusText}>{t("detailMissingText")}</p>
          <div className={styles.buttonRow}>
            <Link className={styles.buttonPrimary} to={localPath("/")}>
              {t("backToGallery")}
            </Link>
          </div>
        </section>
      )}

      {status === "ready" && image && (
        <>
          <section className={styles.pageHero}>
            <span className={styles.eyebrow}>
              {category ? category.name : t("statImages")}
            </span>
            <div className={styles.pillRow}>
              <span className={styles.pill}>{t("free")}</span>
              <span className={styles.pill}>PNG</span>
              <span className={styles.pill}>{t("instantPrint")}</span>
            </div>
            <h1 className={styles.pageTitle}>{image.title}</h1>
            <p className={styles.pageDescription}>{image.description}</p>
            <div className={styles.buttonRow}>
              <a
                className={styles.buttonPrimary}
                href={`/${image.downloadUrl}`}
                rel="noreferrer"
                target="_blank"
              >
                {t("openColoringPage")}
              </a>
              <Link
                className={styles.buttonSecondary}
                to={category ? getCategoryPath(category, locale) : localPath("/")}
              >
                {t("moreFromCollection")}
              </Link>
            </div>
          </section>

          <section className={styles.detailLayout}>
            <div className={styles.detailCanvas} ref={imageCanvasRef}>
              <picture>
                {image.previewUrl && (
                  <source srcSet={`/${image.previewUrl}`} type="image/webp" />
                )}
                <img
                  alt={image.description}
                  className={styles.detailImage}
                  decoding="async"
                  height={image.height}
                  src={`/${image.downloadUrl}`}
                  width={image.width}
                />
              </picture>
            </div>

            <aside className={styles.detailSidebar}>
              <div className={styles.detailPanel}>
                <span className={styles.infoLabel}>{t("imageDetails")}</span>
                <h2 className={styles.detailTitle}>
                  {t("asFreeTemplate", { title: image.title })}
                </h2>
                <p className={styles.detailText}>
                  {t("detailText", {
                    category: category ? category.name : image.tag,
                  })}
                </p>
                <div className={styles.metaList}>
                  <div className={styles.metaItem}>
                    <span className={styles.metaKey}>{t("category")}</span>
                    <span className={styles.metaValue}>
                      {category ? category.name : image.tag}
                    </span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaKey}>{t("file")}</span>
                    <span className={styles.metaValue}>
                      {image.downloadUrl.split("/").pop()}
                    </span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaKey}>{t("use")}</span>
                    <span className={styles.metaValue}>{t("useValue")}</span>
                  </div>
                </div>
              </div>

              <div className={styles.relatedPanel}>
                <span className={styles.infoLabel}>{t("imageSupportTitle")}</span>
                <p className={styles.detailText}>{t("imageSupportText")}</p>
                <div className={styles.relatedList}>
                  <Link
                    className={styles.relatedLink}
                    to={localPath("/so-entstehen-ausmalbilder")}
                  >
                    <span className={styles.relatedTitle}>
                      {t("imageHowMadeLink")}
                    </span>
                  </Link>
                  <Link
                    className={styles.relatedLink}
                    to={localPath("/nutzung-und-lizenz")}
                  >
                    <span className={styles.relatedTitle}>
                      {t("imageLicenseLink")}
                    </span>
                  </Link>
                  <Link className={styles.relatedLink} to={localPath("/drucktipps")}>
                    <span className={styles.relatedTitle}>
                      {t("imagePrintTipsLink")}
                    </span>
                  </Link>
                </div>
              </div>

              {relatedHubs.length > 0 && (
                <div className={styles.relatedPanel}>
                  <span className={styles.infoLabel}>
                    {t("imageRelatedTopicsTitle")}
                  </span>
                  <div className={styles.relatedList}>
                    {relatedHubs.map((hub) => (
                      <Link className={styles.relatedLink} key={hub.id} to={hub.path}>
                        <span className={styles.relatedTitle}>{hub.title}</span>
                        <span className={styles.relatedText}>{hub.description}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {relatedImages.length > 0 && (
                <div className={styles.relatedPanel}>
                  <span className={styles.infoLabel}>{t("sameCollection")}</span>
                  <div className={styles.relatedList}>
                    {relatedImages.map((item) => (
                      <Link
                        className={styles.relatedLink}
                        key={item.downloadUrl}
                        to={getImagePagePath(item, locale)}
                      >
                        <span className={styles.relatedTitle}>{item.title}</span>
                        <span className={styles.relatedText}>
                          {item.description}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </aside>
          </section>

          <section className={styles.benefitGrid} aria-label={t("printTips")}>
            <article className={styles.benefitCard}>
              <h2 className={styles.benefitTitle}>{t("beforePrint")}</h2>
              <p className={styles.benefitText}>{t("beforePrintText1")}</p>
              <p className={styles.benefitText}>{t("beforePrintText2")}</p>
            </article>
            <article className={styles.benefitCard}>
              <h2 className={styles.benefitTitle}>{t("printTip1Title")}</h2>
              <p className={styles.benefitText}>{t("printTip1Text")}</p>
            </article>
            <article className={styles.benefitCard}>
              <h2 className={styles.benefitTitle}>{t("printTip2Title")}</h2>
              <p className={styles.benefitText}>{t("printTip2Text")}</p>
            </article>
            <article className={styles.benefitCard}>
              <h2 className={styles.benefitTitle}>{t("printTip3Title")}</h2>
              <p className={styles.benefitText}>{t("printTip3Text")}</p>
            </article>
          </section>
        </>
      )}
    </SiteShell>
  );
}

export default ImageDetail;
